import React, { useState } from "react";
import { useEnglishText } from "../../../text/useEnglishText.js";

import Modal from "../../../components/ui/Modal";

import {
  Typography,
  Box,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  Grid,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import LockResetIcon from "@mui/icons-material/LockReset";

const LicenseDetailsModal = ({
  open,
  license,
  headerLicense,
  loading,
  onClose,
  onResetPassword,
}) => {
  const { t } = useEnglishText();

  const [resettingPassword, setResettingPassword] = useState(false);

  const licenseNumber =
    license?.LICENSE_NUMBER ||
    headerLicense?.LICENSE_NUMBER ||
    "-";

  const username =
    license?.USER_NAME ||
    license?.DYNASHIP_USER_NAME ||
    headerLicense?.USER_NAME ||
    headerLicense?.DYNASHIP_USER_NAME ||
    "";

  const email =
    license?.EMAIL ||
    license?.USER_EMAIL ||
    headerLicense?.EMAIL ||
    headerLicense?.USER_EMAIL ||
    "";

  const copyToClipboard = async (text) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Unable to copy value:", error);
    }
  };

  const openInNewTab = (url) => {
    if (!url) return;

    const formattedUrl = /^https?:\/\//i.test(url)
      ? url
      : `https://${url}`;

    window.open(formattedUrl, "_blank", "noopener,noreferrer");
  };

  const handleResetPassword = async () => {
    if (resettingPassword) return;

    if (typeof onResetPassword !== "function") {
      console.error(
        "LicenseDetailsModal: onResetPassword is not defined.",
      );
      return;
    }

    try {
      setResettingPassword(true);

      await onResetPassword({
        email,
        username,
        licenseNumber:
          license?.LICENSE_NUMBER ||
          headerLicense?.LICENSE_NUMBER ||
          "",
      });
    } catch (error) {
      // نمایش پیام خطا باید در کامپوننت والد انجام شود.
      console.error(
        "Unable to request password reset:",
        error,
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const handleClose = () => {
    if (resettingPassword) return;

    onClose?.();
  };

  return (
    <Modal
      open={open}
      title={t("licenses.licenses.details.title")}
      onClose={handleClose}
    >
      <Box
        sx={{
          p: 1,
          width: "100%",
          minWidth: {
            xs: "100%",
            sm: 480,
          },
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="h6"
              color="primary"
              fontWeight={700}
            >
              {t("licenses.licenses.details.licenseNo")}:{" "}
              <Box
                component="span"
                sx={{ color: "success.main" }}
              >
                {licenseNumber}
              </Box>
            </Typography>
          </Box>

          <Divider />

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : license ? (
            <Stack spacing={2.5}>
              <Stack spacing={2}>
                <InfoField
                  label={t(
                    "licenses.licenses.details.fields.url",
                    "URL",
                  )}
                  value={license?.URL}
                  onCopy={() =>
                    copyToClipboard(license?.URL)
                  }
                  onOpenLink={() =>
                    openInNewTab(license?.URL)
                  }
                />

                <InfoField
                  label={t(
                    "licenses.licenses.details.fields.username",
                    "Username",
                  )}
                  value={username}
                  onCopy={() =>
                    copyToClipboard(username)
                  }
                />

                <Alert severity="info">
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      {t(
                        "licenses.licenses.details.password.message",
                        "You created this password yourself, and it is the same password you use to sign in to the Admin Center. If you have forgotten it or want to change it, request a reset link and we’ll send it to your email address.",
                      )}
                    </Typography>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        resettingPassword ? (
                          <CircularProgress
                            size={16}
                            color="inherit"
                          />
                        ) : (
                          <LockResetIcon />
                        )
                      }
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                      sx={{
                        alignSelf: "flex-start",
                        minWidth: 210,
                      }}
                    >
                      {resettingPassword
                        ? t(
                            "licenses.licenses.details.password.sending",
                            "Sending Reset Link...",
                          )
                        : t(
                            "licenses.licenses.details.password.resetButton",
                            "Reset or Change Password",
                          )}
                    </Button>
                  </Stack>
                </Alert>
              </Stack>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t(
                      "licenses.licenses.details.fields.activationDate",
                    )}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mt: 0.5 }}
                  >
                    {license?.ACTIVATION_DATE || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t(
                      "licenses.licenses.details.fields.expirationDate",
                    )}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mt: 0.5 }}
                  >
                    {license?.EXPIRATION_DATE || "-"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />

              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.primary"
              >
                {t(
                  "licenses.licenses.details.featuresTitle",
                )}
              </Typography>

              <Grid container spacing={2}>
                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.core",
                  )}
                  value={license?.DYNASHIP_CORE}
                />

                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.enterprise",
                  )}
                  value={license?.DYNASHIP_ENTERPRISE}
                />

                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.agentPortal",
                  )}
                  value={
                    license?.DYNASHIP_AGENT_PORTAL_USER
                  }
                />

                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.customerPortal",
                  )}
                  value={
                    license?.DYNASHIP_CUSTOMER_PORTAL_USER
                  }
                />

                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.functional",
                  )}
                  value={
                    license?.DYNASHIP_FUNCTIONAL_USER
                  }
                />

                <FeatureItem
                  label={t(
                    "licenses.licenses.details.features.additionalCompany",
                  )}
                  value={
                    license?.DYNASHIP_ADDITIONAL_COMPANY
                  }
                />
              </Grid>
            </Stack>
          ) : (
            <Alert severity="warning">
              {t(
                "licenses.licenses.details.noData",
                "License details are not available.",
              )}
            </Alert>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

const InfoField = ({
  label,
  value,
  onCopy,
  onOpenLink,
}) => {
  const hasValue =
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "";

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "action.hover",
          p: 1.2,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            wordBreak: "break-all",
            fontFamily: "monospace",
          }}
        >
          {hasValue ? value : "-"}
        </Typography>

        {hasValue && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 0.5,
            }}
          >
            {onOpenLink && (
              <Tooltip title="Open in New Tab">
                <IconButton
                  size="small"
                  onClick={onOpenLink}
                  color="primary"
                  aria-label="Open in new tab"
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {onCopy && (
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={onCopy}
                  aria-label="Copy"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const FeatureItem = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box
      sx={{
        height: "100%",
        bgcolor: "action.hover",
        p: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        fontWeight={700}
        color="primary"
        sx={{ mt: 0.5 }}
      >
        {value ?? "0"}
      </Typography>
    </Box>
  </Grid>
);

export default LicenseDetailsModal;
